import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Paper, Switch, useTheme } from "@mui/material";
import UserAPI from "../../../api/UserAPI";
import {
  UserAlertSettings,
  UserAlertTypeSetting,
} from "../../../models/User/UserAlertSettings";
import CustomTypography from "../common/form/CustomTypography";
import InlineFormError from "../common/form/InlineFormError";
import SkuCopyRow from "../common/SkuCopyRow";
import { getModernPageColors } from "../../../styles/modernListingPageSx";

const userAPI = new UserAPI();

type Channel = "in_app_enabled" | "email_enabled";

const CHANNELS: { key: Channel; label: string }[] = [
  { key: "in_app_enabled", label: "In-app" },
  { key: "email_enabled", label: "Email" },
];

const errorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return err?.response?.data?.message || err?.message || fallback;
};

/** Outcome of the last switch change, shown under that type's row. */
interface ToggleResult {
  code: string;
  isError: boolean;
  message: string;
}

interface UserAlertSettingsCardProps {
  userId: number;
  /** View-only admins see the switches but cannot change them. */
  disabled?: boolean;
}

/**
 * Which client alerts this user receives, in-app (customer-dashboard) and by email, one row per
 * client alert type. A type never set is off. Each switch saves immediately
 * (`PUT /v1/user/:id/alert-settings`) and is written to the user's activity log.
 *
 * Shown on Add/Edit user for a saved client user. Warns when the customer's Alert switch, or the
 * type for the whole customer, is off (then nobody there gets it, whatever is set here).
 */
const UserAlertSettingsCard: React.FC<UserAlertSettingsCardProps> = ({
  userId,
  disabled = false,
}) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const colors = getModernPageColors(theme);
  const [settings, setSettings] = useState<UserAlertSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  /** `<code>:<channel>` being saved. */
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [toggleResult, setToggleResult] = useState<ToggleResult | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      setSettings(await userAPI.getUserAlertSettings(userId));
    } catch (error) {
      setLoadError(errorMessage(error, "Failed to load alert settings."));
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId > 0) void load();
  }, [userId, load]);

  const toggle = async (
    type: UserAlertTypeSetting,
    channel: Channel,
    value: boolean,
  ) => {
    setSavingKey(`${type.code}:${channel}`);
    setToggleResult(null);
    try {
      const next = await userAPI.setUserAlertSettings(userId, {
        settings: [
          {
            alert_type_code: type.code,
            in_app_enabled: type.in_app_enabled,
            email_enabled: type.email_enabled,
            [channel]: value,
          },
        ],
      });
      setSettings(next);
      const label = CHANNELS.find((item) => item.key === channel)?.label;
      setToggleResult({
        code: type.code,
        isError: false,
        message: `${label} turned ${value ? "on" : "off"}.`,
      });
    } catch (error) {
      setToggleResult({
        code: type.code,
        isError: true,
        message: errorMessage(error, "Failed to save alert settings."),
      });
    } finally {
      setSavingKey(null);
    }
  };

  // A user without a customer (or an API user) has no client alerts: hide the card.
  if (!settings && !isLoading && !loadError) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: colors.surface,
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 1px 3px ${c.shadowVeryLight}`,
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
      }}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={700}
        color={c.darkBlueText}
        sx={{ mb: 0.5 }}
      >
        Alerts
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={c.navyText}
        sx={{ mb: 1.5, opacity: 0.75 }}
      >
        Client alerts this user receives in customer-dashboard (in-app) and by
        email. New users start with everything off. Inventory alerts reach a
        user only for inventories they can see (their orders, or every inventory
        for company admins and master inventories). Changes save immediately.
      </CustomTypography>

      <InlineFormError message={loadError} />

      {isLoading && !settings ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError || !settings ? null : (
        <>
          {!settings.customer_alert_enabled && (
            <InlineFormError
              message="Alerts are off for this user's customer (customer form → Alert), so nothing is sent until it is turned on."
              sx={{ mb: 1.5 }}
            />
          )}
          {settings.types.length === 0 ? (
            <CustomTypography variant="body2" color={c.navyText}>
              No client alert types are configured yet.
            </CustomTypography>
          ) : (
            settings.types.map((type) => (
              <Box
                key={type.code}
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  gap: { xs: 0.5, sm: 2 },
                  py: 1,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  <CustomTypography variant="heading8" color={c.darkBlueText}>
                    {type.description || type.code}
                  </CustomTypography>
                  <SkuCopyRow
                    sku={type.code}
                    copyLabel="Copy code"
                    variant="body3"
                    color={c.navyText}
                  />
                  {!type.customer_type_enabled && (
                    <CustomTypography
                      variant="body3"
                      color={theme.palette.warning.main}
                    >
                      Off for this customer, so this user gets nothing for it.
                    </CustomTypography>
                  )}
                  {toggleResult?.code === type.code &&
                    (toggleResult.isError ? (
                      <InlineFormError
                        message={toggleResult.message}
                        sx={{ mt: 0.5, mb: 0 }}
                      />
                    ) : (
                      <CustomTypography
                        variant="body3"
                        color={theme.palette.success.main}
                      >
                        {toggleResult.message}
                      </CustomTypography>
                    ))}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {CHANNELS.map((channel) => {
                    const key = `${type.code}:${channel.key}`;
                    return (
                      <Box
                        key={channel.key}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <CustomTypography variant="body3" color={c.navyText}>
                          {channel.label}
                        </CustomTypography>
                        <Box
                          sx={{
                            width: 58,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {savingKey === key ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={type[channel.key]}
                              disabled={disabled || savingKey !== null}
                              onChange={(event) =>
                                void toggle(
                                  type,
                                  channel.key,
                                  event.target.checked,
                                )
                              }
                              inputProps={{
                                "aria-label": `${channel.label} ${type.description || type.code}`,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </>
      )}
    </Paper>
  );
};

export default UserAlertSettingsCard;
